import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useBranding } from "@/context/BrandingContext";
import { fileToDataUrl } from "@/lib/img";
import { PageHeader, Loading } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Palette, GanttChartSquare, Save } from "lucide-react";
import { CompletionsLink } from "@/components/CompletionsLink";

export default function Settings() {
  const { refresh } = useBranding();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => { const { data } = await api.get("/branding"); setForm(data); })();
  }, []);

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { set("logo_url", await fileToDataUrl(file, 320)); }
    catch { toast.error("Could not read image"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/branding", form);
      setForm(data);
      await refresh();
      toast.success("Branding saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!form) return <Loading />;

  return (
    <div>
      <PageHeader title="Branding" subtitle="Customise how your platform and public shop look." testid="branding-header">
        <Button onClick={save} disabled={saving} data-testid="save-branding-button"><Save className="h-4 w-4 mr-1.5" />{saving ? "Saving..." : "Save changes"}</Button>
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Card className="p-5 space-y-4">
            <h3 className="font-heading font-semibold flex items-center gap-2"><Palette className="h-4 w-4 text-primary" />Identity</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Platform name</Label><Input value={form.platform_name || ""} onChange={(e) => set("platform_name", e.target.value)} className="mt-1.5" data-testid="branding-name-input" /></div>
              <div><Label>Tagline</Label><Input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="mt-1.5" data-testid="branding-tagline-input" /></div>
            </div>
            <div>
              <Label>Primary colour</Label>
              <div className="flex items-center gap-3 mt-1.5">
                <input type="color" value={form.primary_color || "#2563EB"} onChange={(e) => set("primary_color", e.target.value)} className="h-10 w-14 rounded border border-border cursor-pointer" data-testid="branding-color-input" />
                <Input value={form.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} className="max-w-[140px] font-mono" />
              </div>
            </div>
            <div>
              <Label>Logo</Label>
              <div className="flex items-center gap-4 mt-1.5">
                <div className="h-14 w-14 rounded-lg bg-primary flex items-center justify-center overflow-hidden shrink-0">
                  {form.logo_url ? <img src={form.logo_url} alt="logo" className="h-full w-full object-contain" /> : <GanttChartSquare className="h-7 w-7 text-white" />}
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="branding-logo-upload" />
                  <span className="inline-flex items-center px-3 py-2 rounded-lg border border-border text-sm hover:bg-accent/40"><Upload className="h-4 w-4 mr-1.5" />Upload logo</span>
                </label>
                {form.logo_url && <Button variant="ghost" size="sm" className="text-red-600" onClick={() => set("logo_url", "")}>Remove</Button>}
              </div>
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <h3 className="font-heading font-semibold">Public Shop Hero</h3>
            <div><Label>Headline</Label><Input value={form.shop_hero_title || ""} onChange={(e) => set("shop_hero_title", e.target.value)} className="mt-1.5" data-testid="branding-hero-title-input" /></div>
            <div><Label>Subtitle</Label><Textarea value={form.shop_hero_subtitle || ""} onChange={(e) => set("shop_hero_subtitle", e.target.value)} className="mt-1.5" data-testid="branding-hero-subtitle-input" /></div>
          </Card>
          <CompletionsLink />
        </div>

        <Card className="p-5 h-fit">
          <h3 className="font-heading font-semibold mb-4">Preview</h3>
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="flex items-center gap-2.5 p-3 border-b border-border">
              <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center overflow-hidden">
                {form.logo_url ? <img src={form.logo_url} alt="" className="h-full w-full object-contain" /> : <GanttChartSquare className="h-4 w-4 text-white" />}
              </div>
              <div>
                <p className="font-heading font-bold text-sm leading-none">{form.platform_name || "Platform"}</p>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{form.tagline}</p>
              </div>
            </div>
            <div className="bg-slate-900 p-5">
              <p className="text-white font-heading font-bold text-lg leading-tight">{form.shop_hero_title}</p>
              <p className="text-slate-400 text-xs mt-2 line-clamp-3">{form.shop_hero_subtitle}</p>
              <span className="inline-block mt-3 text-xs text-white px-3 py-1.5 rounded-full bg-primary">Enrol now</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
